import { buildMonthCover, getPeriodKind, spanOf } from './periods';

/*
 * Whether a period is actuals, budget or forecast.
 *
 * The organization keeps whole years (actuals, budget, forecast) plus the
 * month the budget and the forecast start in their first year. A year shown
 * by month or quarter can so be split, e.g. Jan–Sep actuals and Oct–Dec
 * forecast. Status is decided per calendar month; a quarter or year takes the
 * status of its last month (a year holding forecast months is a forecast).
 */

export type PeriodStatus = 'actual' | 'budget' | 'forecast';

export type PeriodStatusSettings = {
  starting_year: number | null;
  actuals: number | null;
  budget: number | null;
  forecast?: number | null;
  /** 1–12, null = January. Months before it in the first budget year are actuals. */
  budget_start_month?: number | null;
  /** 1–12, null = January. Months before it in the first forecast year are budget (or actuals). */
  forecast_start_month?: number | null;
  expanded_periods?: string | null;
};

const absMonth = (y: number, m: number) => y * 12 + (m - 1);

const startMonth = (m: number | null | undefined) => (m != null && m >= 1 && m <= 12 ? m : 1);

/** Calendar years in which the budget and the forecast start. */
export function boundaryYears(s: PeriodStatusSettings): { budgetStartYear: number; forecastStartYear: number } {
  const start = Number(s.starting_year ?? 0);
  const actuals = Number(s.actuals ?? 0);
  const budget = Number(s.budget ?? 0);
  return { budgetStartYear: start + actuals, forecastStartYear: start + actuals + budget };
}

/** Absolute months (year * 12 + month - 1) where budget and forecast begin. */
function boundaries(s: PeriodStatusSettings): { budgetStart: number; forecastStart: number } {
  const { budgetStartYear, forecastStartYear } = boundaryYears(s);
  return {
    budgetStart: absMonth(budgetStartYear, startMonth(s.budget_start_month)),
    forecastStart: absMonth(forecastStartYear, startMonth(s.forecast_start_month)),
  };
}

export function statusOfMonth(year: number, month: number, s: PeriodStatusSettings): PeriodStatus {
  const { budgetStart, forecastStart } = boundaries(s);
  const m = absMonth(year, month);
  if (m >= forecastStart) return 'forecast';
  if (m >= budgetStart) return 'budget';
  return 'actual';
}

/** Status of a period id ('2025', '2025-Q3', '2025-7'): the status of its last month. */
export function periodStatus(periodId: string, s: PeriodStatusSettings): PeriodStatus {
  const { m0, len } = spanOf(periodId);
  const last = m0 + len - 1;
  return statusOfMonth(Math.floor(last / 12), (last % 12) + 1, s);
}

export function statusFlags(status: PeriodStatus): { budget: boolean; forecast: boolean } {
  return { budget: status === 'budget', forecast: status === 'forecast' };
}

/** Start months a year can be split at, given how it is shown ('M', 'Q' or 'Y'). */
export function allowedStartMonths(expansion: string | undefined): number[] {
  if (expansion === 'M') return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  if (expansion === 'Q') return [1, 4, 7, 10];
  return [1];
}

function expansionOfYear(year: number, s: PeriodStatusSettings): string | undefined {
  const index = year - Number(s.starting_year ?? 0);
  return index >= 0 ? s.expanded_periods?.[index] : undefined;
}

/**
 * Problems with the start months, or [] when they are fine. A year can only be
 * split at a month it shows, and the budget can't start after the forecast.
 */
export function startMonthProblems(s: PeriodStatusSettings): string[] {
  const problems: string[] = [];
  const { budgetStartYear, forecastStartYear } = boundaryYears(s);

  const check = (label: string, year: number, month: number | null | undefined) => {
    const m = startMonth(month);
    if (month != null && (month < 1 || month > 12 || !Number.isInteger(month))) {
      problems.push(`${label} start month must be 1–12.`);
      return;
    }
    if (m === 1) return;
    const allowed = allowedStartMonths(expansionOfYear(year, s));
    if (!allowed.includes(m)) {
      problems.push(
        allowed.length === 1
          ? `${label} can only start mid-year when ${year} is shown by month or quarter.`
          : `${label} can only start at the beginning of a quarter in ${year} (months 1, 4, 7 or 10).`,
      );
    }
  };

  check('Budget', budgetStartYear, s.budget_start_month);
  check('Forecast', forecastStartYear, s.forecast_start_month);

  const { budgetStart, forecastStart } = boundaries(s);
  if (budgetStart > forecastStart) problems.push('The budget must start before the forecast.');

  return problems;
}

/**
 * The same settings with start months reset to January where they no longer
 * fit: the year they belonged to moved, or it is no longer shown finely enough.
 */
export function normalizeStartMonths(
  next: PeriodStatusSettings,
  previous?: PeriodStatusSettings,
): { budget_start_month: number | null; forecast_start_month: number | null } {
  const nextYears = boundaryYears(next);
  const prevYears = previous ? boundaryYears(previous) : nextYears;

  const keep = (month: number | null | undefined, year: number, prevYear: number) => {
    if (month == null || month === 1) return null;
    if (year !== prevYear) return null;
    return allowedStartMonths(expansionOfYear(year, next)).includes(month) ? month : null;
  };

  let budget_start_month = keep(next.budget_start_month, nextYears.budgetStartYear, prevYears.budgetStartYear);
  const forecast_start_month = keep(next.forecast_start_month, nextYears.forecastStartYear, prevYears.forecastStartYear);

  // With no budget years both start in the same year; keep the budget first.
  const ordered = { ...next, budget_start_month, forecast_start_month };
  if (boundaries(ordered).budgetStart > boundaries(ordered).forecastStart) budget_start_month = forecast_start_month;

  return { budget_start_month, forecast_start_month };
}

/**
 * Status per absolute month, read from a list of periods that already carry
 * their status (the /periods response). Months outside the list continue the
 * nearest edge: before it actuals, after it forecast. Used by formulas, which
 * only see the periods.
 */
export function monthStatusLookup(
  periods: { period: string; budget: boolean; forecast: boolean }[],
): (absoluteMonth: number) => PeriodStatus {
  const statusById = new Map<string, PeriodStatus>();
  for (const p of periods) {
    statusById.set(p.period, p.forecast ? 'forecast' : p.budget ? 'budget' : 'actual');
  }

  const cover = buildMonthCover(periods.map((p) => p.period).filter((id) => getPeriodKind(id) != null));
  const byMonth = new Map<number, PeriodStatus>();
  for (const c of cover) {
    const status = statusById.get(c.periodId) ?? 'actual';
    for (let m = c.m0; m < c.m0 + c.len; m++) byMonth.set(m, status);
  }

  const first = cover.length ? cover[0].m0 : 0;

  return (m: number) => byMonth.get(m) ?? (m < first ? 'actual' : 'forecast');
}
